import { consoleLog, getNextSimulation } from '@/utils';
import { extractQueryParams, extractRouteParams } from '@/domains/routing';
import { useAppLoading, useSimulationRouting } from '@/hooks';
import { loadSessionScoreView } from '@/services';
import { useEffect, useMemo, useState } from 'react';
import { useGlobalStore } from '@/store';
import { useParams } from 'next/navigation';

/**
 * Feature hook that powers the simulation score view page.
 *
 * Responsibilities:
 * - Read route and query parameters used to identify certification/simulation/session context.
 * - Fetch scorecard data for the active simulation session.
 * - Normalize API scorecard payload into frontend-friendly shape.
 * - Push fetched score data into global store for downstream UI consumers.
 * - Expose display-ready values and UI handlers to the view component.
 *
 * Notes:
 * - This hook intentionally keeps orchestration logic centralized so presentation components
 *   can remain focused on rendering.
 * - Polling is used as a resilience mechanism while score processing is still ongoing.
 */
export const useSimulationScoreViewFeature = () => {
  // ================================
  // Global store bindings
  // ================================
  // Pull both read-only values and mutator actions from the app-wide store.
  // These values represent the user's currently active certification/session context.
  const isLoading = useAppLoading();
  const {
    activeCertification,
    activeScore,
    activeSimulation,
    addScore,
    scores,
    setActiveSessionId,
    setActiveScore,
  } = useGlobalStore();

  // ================================
  // Routing and local state
  // ================================
  // `params` are path params from Next.js App Router.
  const params = useParams();

  // Whether we already have a fully processed score to display.
  // Once true, the polling effect can short-circuit.
  const [scoreProcessed, setScoreProcessed] = useState(false);

  // Progress value used by the processing UI.
  // Currently initialized to a baseline placeholder and passed through to the view.
  const [scoreProcessingProgress, setScoreProcessingProgress] = useState(10);

  // Loading and error state for scorecard retrieval lifecycle.
  const [scorecardsLoading, setScorecardsLoading] = useState(true);
  const [scorecardsError, setScorecardsError] = useState<string | null>(null);

  // Navigation helpers abstract route construction and transitions.
  const { navigateToDashboard, navigateToSimulation } = useSimulationRouting();

  // Timestamp from API for when the scorecard was created.
  // This is surfaced in the score view metadata.
  const [createdDate, setCreatedDate] = useState<string | undefined>(undefined);

  // ================================
  // Parameter extraction
  // ================================
  // Extract typed route params used by the view and return payload.
  const { certificationId, simulationId } = extractRouteParams(params);

  // ================================
  // Derived view values
  // ================================
  // Memoized to avoid unnecessary recalculation/re-renders from object identity changes.
  const certificationTitle = useMemo(() => activeCertification?.title || '', [activeCertification]);
  const simulationTitle = useMemo(() => activeSimulation?.title || '', [activeSimulation]);

  // Determine next simulation within the active certification sequence.
  // Used by the score UI to provide progression navigation.
  const nextSimulation = useMemo(
    () => getNextSimulation({ simulation: activeSimulation, certification: activeCertification }),
    [activeSimulation, activeCertification],
  );

  // ================================
  // Scorecard fetching + polling lifecycle
  // ================================
  // Fetch once immediately, then poll at intervals until a successful scorecard response
  // is received (or until component unmounts/conditions change).
  useEffect(() => {
    // Short-circuit when we already have processed data and simulation context.
    // Prevents unnecessary polling/fetch loops.
    if (scoreProcessed && activeSimulation) {
      return;
    }

    // Keep interval handle in effect scope for cleanup and fetch-level stop conditions.
    let intervalId: NodeJS.Timeout | null = null;

    // Performs one scorecard fetch attempt.
    // Returns `true` on successful retrieval + normalization, else `false`.
    const fetchScorecards = async () => {
      // Extract query params directly from the browser URL when running client-side.
      // Guarded for SSR safety.
      const urlSearchParams = extractQueryParams(
        typeof window !== 'undefined' ? window.location.search : '',
      );

      // Session identifier is required for scorecard fetches.
      // If absent, fetch attempts are aborted and warning is logged.
      const sessionId = urlSearchParams['sessionId'] || null;
      let sessionIdToFetch: number;

      // Resolve target session id for the API request.
      if (sessionId) {
        consoleLog.log('Extracted sessionId from URL:', sessionId);
        sessionIdToFetch = parseInt(sessionId);

        // Keep store session state synchronized with URL-derived session id.
        setActiveSessionId(sessionIdToFetch);
      } else {
        console.warn('No active session ID available. Will retry...');
        return false;
      }

      // Enter loading state for this fetch cycle and clear stale errors.
      setScorecardsLoading(true);
      setScorecardsError(null);

      try {
        consoleLog.log('Fetching scorecard for session:', sessionIdToFetch);

        const result = await loadSessionScoreView(sessionIdToFetch);

        const fetchedScore = result.score;
        const scoreSimulationId =
          fetchedScore?.simulationId || simulationId || activeSimulation?.id?.toString();

        const inferredRuntimeRatingCutoffs =
          fetchedScore?.runtimeRatingCutoffs ||
          (Array.isArray(scores)
            ? scores.find((existingScore) => {
                return (
                  existingScore.simulationId === scoreSimulationId &&
                  Boolean(existingScore.runtimeRatingCutoffs)
                );
              })?.runtimeRatingCutoffs
            : undefined);

        const normalizedScore = inferredRuntimeRatingCutoffs
          ? {
              ...fetchedScore,
              runtimeRatingCutoffs: inferredRuntimeRatingCutoffs,
            }
          : fetchedScore;

        // Persist score in collection and set as active item for immediate display.
        addScore(normalizedScore);
        setActiveScore(normalizedScore);

        // Track metadata + state flags used by presentation layer.
        setCreatedDate(result.createdAt);
        setScoreProcessed(true);
        setScorecardsLoading(false);

        // Stop polling once a successful scorecard has been retrieved.
        if (result && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }

        return true;
      } catch (error) {
        // Preserve actionable error for UI and log full detail for debugging.
        setScorecardsError(error instanceof Error ? error.message : 'Failed to fetch scorecards');
        console.error('Error fetching scorecards:', error);
        return false;
      }
    };

    // Immediate attempt on mount/dependency changes.
    fetchScorecards();

    // Poll every 20 seconds until success, even if sessionId is initially missing.
    intervalId = setInterval(async () => {
      const success = await fetchScorecards();
      if (success && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    }, 20000);

    // Cleanup interval when effect dependencies change or component unmounts.
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    activeSimulation,
    addScore,
    scoreProcessed,
    scores,
    setActiveScore,
    setActiveSessionId,
    simulationId,
  ]);

  // ================================
  // UI event handlers
  // ================================
  // Handler for retaking the current roleplay simulation.
  // Requires both active certification and simulation context to generate route.
  const handleRetakeRolePlayClick = () => {
    if (!activeCertification || !activeSimulation) {
      console.warn('Cannot navigate: missing certification or simulation data');
      return;
    }

    return navigateToSimulation(activeCertification, activeSimulation);
  };

  // ================================
  // Public API for consuming view
  // ================================
  // Return a flat contract that the UI layer can consume directly.
  return {
    activeScore,
    activeSimulation,
    certificationId,
    certificationTitle,
    createdDate,
    handleRetakeRolePlayClick,
    isLoading,
    navigateToDashboard,
    nextSimulation,
    scorecardsError,
    scorecardsLoading,
    scoreProcessingProgress,
    simulationId,
    simulationTitle,
  };
};
